import { useCallback, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { ChevronDown, Clock, LayoutGrid, MapPin, Plus } from 'lucide-react'
import { useNavigate } from '@tanstack/react-router'
import { toast } from 'sonner'
import { groupDocsByLocation, recentDocs, sortByRank } from '../utils/spaceDocs'
import { SpaceDocsTable } from './SpaceDocsTable'
import type { PageDocument } from '@/lib/db/rxdb/hooks/entities/pages'
import type { ProjectDocument } from '@/lib/db/rxdb/schemas/project'
import { usePageContents, usePageMutations, usePages, useProjects } from '@/lib/db/rxdb/hooks'
import useLoginStore from '@/store/LoginStore'
import { LocationSwatch } from '@/components/app/sidebars/spaces/SpaceSwatch'
import { DocCard } from '@/routes/features/pages/components/DocsIndex'
import { docRoute } from '@/routes/features/pages/utils/docRoute'
import { extractPlainTextFromTiptap } from '@/routes/features/tasks/utils/editorUtils'
import { generateRankBetween } from '@/lib/utils/lexorank'
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover'
import { cn } from '@/lib/utils'

type DocsMode = 'location' | 'recent' | 'cards'
const MODES: Array<DocsMode> = ['location', 'recent', 'cards']
const MODE_KEY = 'moly-space-docs-mode'

function readMode(): DocsMode {
  try {
    const stored = localStorage.getItem(MODE_KEY)
    return MODES.includes(stored as DocsMode) ? (stored as DocsMode) : 'location'
  } catch {
    return 'location'
  }
}

/**
 * All docs in a space (design 22a): every doc in the space and its projects,
 * grouped by where it lives, most recent first, or as cards. It is the
 * space's Docs tab (`?tab=docs`), linked as "All docs in …" from the sidebar.
 */
export function SpaceAllDocsView({ space, workspaceSlug }: { space: ProjectDocument; workspaceSlug: string }) {
  const { t } = useTranslation('projects')
  const navigate = useNavigate()
  const workspaceId = useLoginStore((s) => s.workspaceId)
  const { projects } = useProjects()
  const { pages } = usePages()
  const [mode, setModeState] = useState<DocsMode>(readMode)

  const scoped = useMemo(
    () => projects.filter((p) => !p.isDeleted && (!workspaceId || p.envId === workspaceId)),
    [projects, workspaceId],
  )
  const spaceProjects = useMemo(() => sortByRank(scoped.filter((p) => p.parentProjectId === space.id)), [scoped, space.id])
  const locationsById = useMemo(
    () => new Map<string, ProjectDocument>([[space.id, space], ...spaceProjects.map((p) => [p.id, p] as const)]),
    [space, spaceProjects],
  )
  const groups = useMemo(() => groupDocsByLocation(space, spaceProjects, pages), [space, spaceProjects, pages])
  const recent = useMemo(() => recentDocs(new Set(locationsById.keys()), pages), [locationsById, pages])
  const total = recent.length

  const setMode = useCallback((next: DocsMode) => {
    setModeState(next)
    try {
      localStorage.setItem(MODE_KEY, next)
    } catch {
      // A preference only.
    }
  }, [])

  const openDoc = useCallback(
    (page: PageDocument) => {
      const route = docRoute(workspaceSlug, page, scoped)
      if (route) void navigate(route)
    },
    [navigate, scoped, workspaceSlug],
  )

  return (
    <div className="h-full overflow-y-auto" data-testid="space-all-docs">
      <div className="mx-auto w-full max-w-[980px] px-4 py-4 md:px-6 md:py-6">
        <div className="mb-4 flex flex-wrap items-center gap-2">
          <h1 className="mr-auto flex min-w-0 items-baseline gap-2">
            <span className="truncate text-[15px] font-semibold">{t('docs.allDocs.title')}</span>
            <span className="text-[13px] tabular-nums text-muted-foreground">{t('docs.count', { count: total })}</span>
          </h1>
          <ModeSwitch mode={mode} onChange={setMode} />
          <NewDocInMenu space={space} projects={spaceProjects} pages={pages} onCreated={openDoc} />
        </div>
        {mode === 'cards' ? (
          <DocCardsByLocation groups={groups} space={space} onOpen={openDoc} />
        ) : (
          <SpaceDocsTable
            space={space}
            groups={mode === 'location' ? groups : undefined}
            recent={mode === 'recent' ? recent : undefined}
            locationsById={locationsById}
            onOpen={openDoc}
          />
        )}
      </div>
    </div>
  )
}

/** By location / Recent / Cards. Icons only on narrow screens. */
function ModeSwitch({ mode, onChange }: { mode: DocsMode; onChange: (mode: DocsMode) => void }) {
  const { t } = useTranslation('projects')
  const icons = { location: MapPin, recent: Clock, cards: LayoutGrid } as const
  return (
    <div role="radiogroup" aria-label={t('docs.viewAs')} className="flex items-center gap-0.5 rounded-lg bg-secondary p-0.5">
      {MODES.map((option) => {
        const Icon = icons[option]
        const label = t(`docs.allDocs.mode.${option}`)
        return (
          <button
            key={option}
            type="button"
            role="radio"
            aria-checked={mode === option}
            aria-label={label}
            onClick={() => onChange(option)}
            className={cn(
              'flex h-7 items-center gap-1.5 rounded-md px-2 text-xs font-medium transition-colors',
              mode === option ? 'bg-card text-foreground shadow-xs' : 'text-muted-foreground hover:text-foreground',
            )}
          >
            <Icon className="size-3.5 sm:hidden" aria-hidden />
            <span className="hidden sm:inline">{label}</span>
          </button>
        )
      })}
    </div>
  )
}

/** "New doc ▾" → "New doc in…": the space itself or one of its projects. */
function NewDocInMenu({
  space,
  projects,
  pages,
  onCreated,
}: {
  space: ProjectDocument
  projects: Array<ProjectDocument>
  pages: Array<PageDocument>
  onCreated: (page: PageDocument) => void
}) {
  const { t } = useTranslation('projects')
  const { createPage } = usePageMutations()
  const [open, setOpen] = useState(false)

  const create = async (location: ProjectDocument) => {
    setOpen(false)
    const roots = pages
      .filter((p) => !p.isDeleted && p.projectId === location.id && !p.parentPageId)
      .sort((a, b) => (a.sortOrder && b.sortOrder ? a.sortOrder.localeCompare(b.sortOrder) : a.createdOn - b.createdOn))
    try {
      const created = await createPage({
        projectId: location.id,
        parentPageId: null,
        title: '',
        slug: crypto.randomUUID().slice(0, 8),
        icon: null,
        iconType: null,
        sortOrder: generateRankBetween(roots.at(-1)?.sortOrder ?? null, null),
      })
      onCreated(created)
    } catch {
      toast.error(t('sidebar.toast.pageCreateFailed'))
    }
  }

  const item = (location: ProjectDocument, subtitle?: string) => (
    <button
      key={location.id}
      type="button"
      role="menuitem"
      onClick={() => void create(location)}
      className="flex w-full items-start gap-2.5 rounded-md px-2.5 py-2 text-left text-[13px] transition-colors hover:bg-secondary focus-visible:bg-secondary focus-visible:outline-none"
    >
      <LocationSwatch location={location} size={14} className="mt-0.5" />
      <span className="min-w-0">
        <span className="block truncate font-medium">{location.name}</span>
        {subtitle && <span className="block text-[11.5px] leading-snug text-muted-foreground">{subtitle}</span>}
      </span>
    </button>
  )

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          data-testid="new-doc-in"
          className="flex h-8 items-center gap-1.5 rounded-lg bg-foreground px-3 text-[13px] font-medium text-background transition-opacity hover:opacity-90"
        >
          <Plus className="size-3.5" />
          <span className="hidden sm:inline">{t('docs.newDoc')}</span>
          <ChevronDown className="size-3.5 opacity-70" />
        </button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-[264px] rounded-xl p-1.5" role="menu" aria-label={t('docs.allDocs.newDocIn')}>
        <p className="px-2.5 pb-1 pt-1.5 text-[10.5px] font-semibold uppercase tracking-[0.1em] text-muted-foreground">
          {t('docs.allDocs.newDocIn')}
        </p>
        {item(space, t('docs.allDocs.spaceDocHint'))}
        {projects.map((p) => item(p))}
      </PopoverContent>
    </Popover>
  )
}

/** Cards (the docs index grid), still grouped by where each doc lives. */
function DocCardsByLocation({
  groups,
  space,
  onOpen,
}: {
  groups: ReturnType<typeof groupDocsByLocation>
  space: ProjectDocument
  onOpen: (page: PageDocument) => void
}) {
  const { t } = useTranslation('projects')
  const { contents } = usePageContents()
  const previewByPage = useMemo(() => {
    const map = new Map<string, string>()
    for (const c of contents) map.set(c.pageId, (c.plainText ?? extractPlainTextFromTiptap(c.content)).trim())
    return map
  }, [contents])

  return (
    <div className="flex flex-col gap-6">
      {groups.map((group) => {
        const topLevel = group.pages
          .filter((p) => !p.parentPageId)
          .sort((a, b) => (a.sortOrder ?? '').localeCompare(b.sortOrder ?? '') || a.createdOn - b.createdOn)
        const childCount = (id: string) => group.pages.filter((p) => p.parentPageId === id).length
        return (
          <section key={group.location.id} aria-label={group.location.name}>
            <h2 className="mb-3 flex items-center gap-2 text-[13px]">
              <LocationSwatch location={group.location} size={14} />
              <span className="font-semibold">{group.kind === 'space' ? t('docs.allDocs.spaceDocs') : group.location.name}</span>
              <span className="tabular-nums text-muted-foreground">{group.pages.length}</span>
              <span className="truncate text-[12px] text-muted-foreground">
                {group.kind === 'space' ? t('docs.allDocs.visibleTo', { name: space.name }) : t('docs.allDocs.projectDocs')}
              </span>
            </h2>
            {topLevel.length === 0 ? (
              <p className="text-[13px] text-muted-foreground">{t('docs.allDocs.noSpaceDocs')}</p>
            ) : (
              <ul className="grid gap-4 sm:grid-cols-2 xl:grid-cols-3">
                {topLevel.map((page) => (
                  <li key={page.id}>
                    <DocCard
                      page={page}
                      preview={previewByPage.get(page.id) ?? ''}
                      subpages={childCount(page.id)}
                      onOpen={() => onOpen(page)}
                    />
                  </li>
                ))}
              </ul>
            )}
          </section>
        )
      })}
    </div>
  )
}
